import { ReactComponent as IconChevronDown } from "@/assets/svgs/chevron-down.svg";
import { assetUrl } from '@/utils/asset-url';
import { ReactComponent as IconChevronRight } from "@/assets/svgs/chevron-right.svg";
import { ReactComponent as IconClose } from "@/assets/svgs/icon-close.svg";
import { ReactComponent as IconDefaultDataElement } from "@/assets/svgs/icon-data.svg";
import { ReactComponent as IconGear } from "@/assets/svgs/icon-gear.svg";
import { ReactComponent as IconPitstop } from '@/assets/svgs/icon-pitstop.svg';
import { ReactComponent as IconSearch } from '@/assets/svgs/icon-search.svg';
import { ReactComponent as IconTick } from '@/assets/svgs/tick.svg';
import Breadcrumbs from "@/components/Breadcrumbs";
import Button from "@/components/Button";
import CardConfigure from "@/componentsDS/Modal/CardConfigure";
import Modal from "@/componentsDS/Modal/Modal";
import Input from "@/components/Input/index";
import Popover from '@/components/Popover';
import Portal from "@/components/Portal";
import SelectorBox2 from "@/components/SelectorBox2";
import StepList from '@/components/StepList/index';
import Toast from "@/components/Toast";
import Stepper from "@/componentsDS/Stepper/Stepper";
import { featureConstant } from "@/constants/configuration/feature";
import { EnrolmentTypes } from '@/constants/use-case';
import useDataElementDetails from "@/hooks/use-data-elements-details";
import useModal from "@/hooks/use-modal";
import usePage from "@/hooks/use-page";
import usePitstop from '@/hooks/use-pitstop/index';
import usePrincipals from "@/hooks/use-principal/index";
import useStepList from '@/hooks/use-step-list';
import useSystem from "@/hooks/use-sys";
import useToast from "@/hooks/use-toast";
import { useNavigate, useParams } from "@/lib/router-dom/index";
import PrincipalTableList from "@/pages/use-cases/agent-principal/principal-table";
import API_ENROLMENTS from "@/services/api/enrolment";
import { feature } from "@/utils/feature-verifier";
import rem from "@/utils/rem";
import { getSystemList, isSubmitBtnDisabled } from '@/utils/use-case';
import { getUseCaseId } from "@/utils/use-case-storage";
import classname from "classnames";
import produce from "immer";
import { uniqueId } from "lodash";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

const Page = () => {
	/*=================================================
		General
	/================================================*/

	const { useCaseId, dataElementId, enrolmentType } = useParams();
	const useCase = useMemo(() => getUseCaseId(useCaseId), [useCaseId]);

	const user = useSelector((state) => state.auth.user);
	const isAgentPrincipalPhase2Enabled = feature(featureConstant.AGENT_PRINCIPAL_PHASE_2)

	const toastType = {
		success: "success",
		error: "error",
	};

	const [pageTitle] = useState("Add Pitstop");
	const [subPageTitle] = useState(useCase.name);

	const { breadcrumbsItems } = usePage({
		title: pageTitle,
		breadcrumbs: ["Dashboard", subPageTitle, pageTitle],
	});



	const { goTo } = useNavigate();
	const type = enrolmentType.toUpperCase();
	const isOnBehalfOfPrincipal = (type === EnrolmentTypes.provideOnBehalfOfPrincipal || type === EnrolmentTypes.consumeOnBehalfOfPrincipal)

	const [required, setRequired] = useState(false);
	const [optional, setOptional] = useState(false);

	const [loading, setLoading] = useState(false)

	/*=================================================
		API
	/================================================*/

	const { toastList, addToastMessage } = useToast();

	const {
		find: findSys,
		items: systemItems,
		setItems: setSystem,
		view: viewSystem,
		currentItem: currenSystemItem,
		error: errorSystem,
	} = useSystem();
	const {
		currentItem,
		setCurrentItem,
		fetchDataElementsDetail,
		error,
	} = useDataElementDetails();
	const { fetch: fetchPrincipals, items: principalItems, setItems: setPrincipalItems } = usePrincipals(
		{ cacheFetchedItem: { identifier: 'id', keys: ['$selected'] } }
	)

	const params = {
		useCaseId,
		dataElementId,
		type: enrolmentType,
	};

	async function fetchSystem() {
		const data = await fetchDataElementsDetail(params);
		setCurrentItem(data);

		let getSystems = {
			useCaseId: useCaseId,
			dataElementId: dataElementId,
			type: enrolmentType.toUpperCase(),
		};

		if (
			enrolmentType.toUpperCase() === EnrolmentTypes.provider ||
			enrolmentType.toUpperCase() === EnrolmentTypes.consumer
		) {
			getSystems = {
				...getSystems,
				of: "ALL",
			};
		}

		const systems = await findSys(getSystems);

		const registeredSys = data?.system.filter(obj => obj.enrolStatus === "APPROVED");
		const newSystems = produce(systems, (draft) => {
			const unregisteredSys = draft.filter((sys) => !registeredSys?.some(s => s.id === sys.id));
			if (unregisteredSys?.length === 1) {
				const index = draft.findIndex((sys) => sys.id === unregisteredSys[0].id);
				draft[index].$selected = true;
			}
		});

		setSystem(newSystems);
	}

	const { items: pitstopItems, fetch: fetchPitstop } = usePitstop()

	useEffect(async () => {
		try {
			await fetchSystem();
			await fetchPitstop()
		} catch (err) {
			console.error({ err });
		}
	}, []);

	useEffect(() => {
		addToastMessage(errorSystem, toastType.error);
	}, [errorSystem]);

	const pitstopList = pitstopItems?.map((item) => {
		return {
			id: item.systemId,
			name: item.systemName
		}
	})

	/*=================================================
		Step
	/================================================*/
	const firstStepList = [
		{
			stepTitle: "Choose Pitstop"
		},
		{
			stepTitle: "Choose Principal"
		},
		{
			stepTitle: "Review & Confirm"
		}

	]
	const secondStepList = [
		{
			stepTitle: "Choose Pitstop"
		},
		{
			stepTitle: "Review & Confirm"
		},
	]

	const steps = isOnBehalfOfPrincipal ? firstStepList:secondStepList;

	const { step, goToStep, prevStep, nextStep } = useStepList(steps)

	/*=================================================
		Modal
	/================================================*/

	const {
		visible: visibleSystemInfo,
		show: showSystemInfo,
		hide: hideSystemInfo,
	} = useModal();

	/*=================================================
		Search
	/================================================*/

	const [searchBoxValue, setSearchBoxValue] = useState("")

	function handleSearch(e) {
		setSearchBoxValue(e.target.value)
	}

	/*=================================================
		Sort
	/================================================*/

	const [sortConfig, setSortConfig] = useState({ sortBy: "name", direction: 'asc' });

	/*=================================================
		Principal List & Pagination
	/================================================*/

	function getSortedData(_searchTerm = searchBoxValue, sortBy = sortConfig?.sortBy, direction = sortConfig?.direction) {
		if (isOnBehalfOfPrincipal) {
			fetchPrincipals({
				search: _searchTerm?.length > 0 ? _searchTerm : null,
				sortBy: sortBy?.length > 0 ? sortBy : null,
				direction: sortBy ? (direction ? "asc" : "desc") : null,
			})
		}
	}

	/*=================================================
		System List
	/================================================*/

	const SystemList = ({ dataItem }) => {
		const updatedSystem = currentItem?.system.filter(obj => obj.enrolStatus === "APPROVED");
		const updatedPitstopArray = dataItem?.filter(objFromA => !updatedSystem?.find(objFromB => {
			return objFromA.id === objFromB.id
		}))

		async function handleCheckInfo(item) {
			viewSystem(item.id);
			showSystemInfo();
		}

		const handleUpdateSelect = (item) => {
			const index = systemItems?.findIndex((sys) => sys.id === item.id);
			const updatedItems = produce(systemItems, (draft) => {
				draft[index].$selected = !draft[index].$selected;
			});

			const updatedSysList = getSystemList(
				updatedItems,
				pitstopList,
				enrolmentType,
			);
			setSystem(updatedSysList);
		};

		const list = updatedPitstopArray?.map((sys, index) => {
			const id = uniqueId("selector-item-");
			return (
				<div className="request-enrolment-selectors__col" key={id}>
					<SelectorBox2
						label={sys?.systemName}
						sublabel={sys?.organisation.name}
						image={sys?.organisation.iconUrl}
						submit={() => {
							handleUpdateSelect(sys);
						}}
						active={sys?.$selected}
						handleCheckInfo={() => handleCheckInfo(sys)}
					/>
				</div>
			);
		});

		return (
			<div className="request-enrolment-selectors__cols">
				{list.length > 0 ? (
					list
				) : (
					<div className="request-enrolment-selectors__col">
						No data elements to enroll
					</div>
				)}
			</div>
		);
	};

	/*=================================================
		Confirmation
	/================================================*/

	const selectedSystemItem = systemItems.filter(sys => sys.$selected)
	const providerPitstopList = selectedSystemItem?.filter((sys) => {
		return !pitstopList?.some(pitstop => {
			return sys.id === pitstop.id
		})
	})

	function handleCopyToContributor(item) {
		const index = systemItems?.findIndex(sys => sys.id === item.id);
		const updatedDataItems = produce(systemItems, draft => {
			draft[index].$ccToContributor = !draft[index].$ccToContributor

			if (pitstopList?.length === 1 && draft[index].$ccToContributor) {
				draft[index].contributorSystemId = pitstopList[0].id
			}

			if (!draft[index].$ccToContributor) {
				draft[index].contributorSystemId = undefined
				draft[index].contributorSystemName = ""
				draft[index].$sysOptionsVisible = false
			}
		})

		setSystem(updatedDataItems)
	}

	function handleDropdownVisibility(item) {
		const index = systemItems?.findIndex(sys => sys.id === item.id);
		const updatedItems = produce(systemItems, draft => {
			draft[index].$sysOptionsVisible = !draft[index].$sysOptionsVisible
		})

		setSystem(updatedItems)
	}

	function updateSelectedPitstop(option, item) {
		const index = systemItems?.findIndex(sys => sys.id === item.id);
		const updatedItems = produce(systemItems, draft => {
			draft[index].$sysOptionsVisible = !draft[index].$sysOptionsVisible
			draft[index].contributorSystemId = option.id
			draft[index].contributorSystemName = option.name
		})

		setSystem(updatedItems)
	}


	const requiredAction = classname({
		"enrolment-request-field-action": true,
		"enrolment-request-field-action--active ": required,
	});

	const optionalAction = classname({
		"enrolment-request-field-action": true,
		"enrolment-request-field-action--active": optional,
	});

	const CopyCheckbox = ({ selected, handleUpdateItemSelect, disabled }) => {
		const CheckBoxClass = classname({
			"use-case-request__checkbox": true,
			"use-case-request__checkbox--active": selected,
			"use-case-request__checkbox--disabled": disabled,
		})

		return (
			<div data-testid='checkbox-element' className={CheckBoxClass}
				onClick={handleUpdateItemSelect}
				tabIndex={0}
				onKeyPress={handleUpdateItemSelect}
			>
				{selected ? <IconTick /> : null}
			</div>
		)
	}


	const Confirmation = () => {
		const RequestFields = ({ field, schema, showBoldText }) => {
			if (!Array.isArray(field)) {
				return "";
			}

			const items = field.map((item, index) => {
				const id = uniqueId("request-field-");
				return (
					<div className="container-content-item__col" key={id}>
						<span>{item.name}</span>
					</div>
				);
			});
			return items;
		};

		const renderSelectedRow = (items) => {
			return items?.length && items.filter((s) => s.$selected === true).map((s, index) => {
				return (
					<Fragment key={index}>
						<div className="layout-request__tile__content__row">
							<div className="layout-request__tile__content__col">
								<div
									className="layout-request__tile__content__avatar"
									style={{
										backgroundImage: `url(${s?.organisation?.iconUrl})`,
									}}
								>
									{!s?.organisation?.iconUrl && <IconPitstop />}
								</div>
								<div className="enrolment-request__content__system-info">
									<div className="enrolment-request__systme-name">
										{s?.name}
									</div>
									{enrolmentType.toUpperCase() === EnrolmentTypes.provider && !pitstopList?.some(pitstop => pitstop.id === s.id) && (
										<div className="enrolment-request__system-selection">
											<div className="enrolment-request__system-selection-checkbox">
												<CopyCheckbox selected={s.$ccToContributor} handleUpdateItemSelect={() => handleCopyToContributor(s)} />
											</div>
											<div className="enrolment-request__system-selection-description">
												<span>Send transaction copies to Pitstop&nbsp;</span>
												{pitstopList?.length === 1 && (
													<span className="enrolment-request__system-selection-description--bold">{`"${pitstopList[0].name}"`}</span>
												)}
											</div>
											{pitstopList?.length > 1 && (
												<div className="enrolment-request__dropdown-container">
													<Popover visible={s.$sysOptionsVisible}>
														<Popover.Trigger>
															<div
																data-testid='pitstop-dropdow'
																className="enrolment-request__dropdown enrolment-request__dropdown-pitstop"
																onKeyPress={() => handleDropdownVisibility(s)}
																tabIndex={0}
																onClick={() => handleDropdownVisibility(s)}
															>
																<div className="enrolment-request__dropdown-placeholder">{s.contributorSystemName ? s.contributorSystemName : "Select Pitstop"}</div>
																<div><IconChevronDown /></div>
															</div>

														</Popover.Trigger>
														<Popover.Content>
															<div className="enrolment-request__dropdown-content enrolment-request__dropdown-content__pitstop">
																{pitstopList?.map((option) => (
																	<div
																		className="enrolment-request__dropdown-item"
																		key={option.id}
																		tabIndex={0}
																		onKeyPress={() => { updateSelectedPitstop(option, s) }}
																		onClick={() => { updateSelectedPitstop(option, s) }}
																	>
																		{option.name}
																	</div>
																))}
															</div>
														</Popover.Content>
													</Popover>
												</div>
											)}
										</div>
									)}
								</div>

							</div>
						</div>
					</Fragment>
				);
			});
		}

		return (
			<div className="enrolment-request_confirmation__container">
				<div className="layout-request-block enrolment-request_confirmation_left">
					<div className="layout-request__tiles">
						<div className="layout-request__tile">
							<div className="layout-request__tile__content">
								<div className="layout-request__tile__content__header request-enrolment-confirmation-header">
									<div className="layout-request__title__content__header__left">
										<div className="request-title request-enrolment-confirmation-header-title">
											Selected Data Element
										</div>
									</div>
								</div>
								<div className="enrolment-request__tile__content">
									<div className="layout-request__tile__content__sub__header enrolment-request__tile__content__sub__header">
										<div className="layout-request__tile__content__sub__header__icon layout-request__tile__content__sub__header__icon--purple">
											<IconDefaultDataElement />
										</div>
										<div className="enrolment-request__tile__content__sub__header__text">
											<div className="layout-request__tile__content__sub__header__text__top">
												<div className="request-title">
													{
														currentItem?.dataElement
															.dataElementName
													}
												</div>
											</div>
											<div className="layout-request__tile__content__sub__header__text__bottom">
												<div className="request-title-sub__text">
													{getTypeName()}
												</div>
											</div>
										</div>
									</div>
									<div className="layout-request__tile__content__sub__header enrolment-request__tile__content__sub__header">
										<div className="enrolment-request__tile__content__sub__header__text">
											<div className="layout-request__tile__content__sub__header__text__top">
												<div className="request-title-sub__text--link request-title-sub__text--grey80">
													<span>Data Fields</span>
												</div>
											</div>
										</div>
									</div>
									<div className="layout-request__tile__content__sub__header enrolment-request__tile__content__sub__header">
										<div className="enrolment-request__tile__content__sub__header__text">
											<div className="layout-request__tile__content__sub__header__text__top">
												<div className="layout-request__tile__content__sub__header__text__top__left">
													<div className="request-title-sub__text--link">
														<span>Required</span>
													</div>
												</div>
												<div className="layout-request__tile__content__sub__header__text__top__right">
													<span className="enrolment-request-field-count">
														{
															currentItem
																?.requiredFields
																.length
														}
													</span>
													<span
														className={
															requiredAction
														}
														onClick={() =>
															setRequired(
																!required
															)
														}
													>
														<IconChevronRight />
													</span>
												</div>
											</div>
											<div className="enrolment-request__tile__content__sub__header__text__bottom">
												{required && (
													<RequestFields
														field={
															currentItem?.requiredFields
														}
													/>
												)}
											</div>
										</div>
									</div>
									<div className="layout-request__tile__content__sub__header enrolment-request__tile__content__sub__header">
										<div className="enrolment-request__tile__content__sub__header__text">
											<div className="layout-request__tile__content__sub__header__text__top">
												<div className="layout-request__tile__content__sub__header__text__top__left">
													<div className="request-title-sub__text--link">
														<span>Optional</span>
													</div>
												</div>
												<div className="layout-request__tile__content__sub__header__text__top__right">
													<span className="enrolment-request-field-count">
														{
															currentItem
																?.optionalFields
																.length
														}
													</span>
													<span
														className={
															optionalAction
														}
														onClick={() =>
															setOptional(
																!optional
															)
														}
													>
														<IconChevronRight />
													</span>
												</div>
											</div>
											<div className="enrolment-request__tile__content__sub__header__text__bottom">
												{optional && (
													<RequestFields
														field={
															currentItem?.optionalFields
														}
													/>
												)}
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div className="layout-request-block enrolment-request_confirmation_right">
					{isOnBehalfOfPrincipal &&
						<div className="layout-request__tiles">
							<div className="layout-request__tile">
								<div className="layout-request__tile__content">
									<div className="layout-request__tile__content__header request-enrolment-confirmation-header">
										<div className="layout-request__title__content__header__left">
											<div className="request-title request-enrolment-confirmation-header-title">
												Selected Principal
											</div>
										</div>
										<div className="layout-request__title__content__header__right">
											<Button
												text="Edit"
												ghost
												submit={() => {
													goToStep(2);
												}}
											/>
										</div>
									</div>

									<div className="layout-request__title__content__rows">
										{renderSelectedRow(principalItems)}
									</div>
								</div>
							</div>
						</div>
					}

					<div className="layout-request__tiles">
						<div className="layout-request__tile">
							<div className="layout-request__tile__content">
								<div className="layout-request__tile__content__header request-enrolment-confirmation-header">
									<div className="layout-request__title__content__header__left">
										<div className="request-title request-enrolment-confirmation-header-title">
											Selected System
										</div>
									</div>
									<div className="layout-request__title__content__header__right">
										<Button
											text="Edit"
											ghost
											submit={() => {
												goToStep(1);
											}}
										/>
									</div>
								</div>

								<div className="layout-request__title__content__rows">
									{renderSelectedRow(systemItems)}
									{enrolmentType.toUpperCase() === EnrolmentTypes.provider && providerPitstopList.length > 0 && (
										<div className="layout-request__tile__content__row">
											<div data-testid="note-msg" className="enrolment-request__note-banner">
												<span className="enrolment-request__note-banner__text--bold">{"Note: "}</span>
												<span className="enrolment-request__note-banner__text">A copy of the data element will be sent to the selected pitstop when data provider shares this Data Element on behalf of your organization.</span>
											</div>
										</div>
									)
									}
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	};

	/*=================================================
		Navigate
	/================================================*/

	function onBack() {
		goTo(-1)
	}

	/*=================================================
		Additional
	/================================================*/

	async function confirm() {
		if (loading) {
			return
		}

		setLoading(true)

		const getSystemIds = systemItems
			.filter((sys) => sys.$selected === true)
			.map((sys) => sys.id);

		const getOrganisationIds = systemItems.filter(sys => sys.$selected === true).map(sys => sys.organisation.id)

		const payload = getSystemIds.map((id) => {
			return {
				useCaseId: useCaseId,
				dataElementId: currentItem?.dataElement.dataElementId,
				systemId: id,
				orgId: user.organizationId,
				type: enrolmentType.toUpperCase(),
			};
		});

		const systemPayload = payload.map(payloadItem => {
			const sysItem = systemItems.filter(sys => sys.id === payloadItem.systemId)
			if (sysItem[0].hasOwnProperty('$ccToContributor')) {
				payloadItem = { ...payloadItem, ccToContributor: sysItem[0].$ccToContributor, contributorSystemId: sysItem[0].contributorSystemId }
			}
			if (isOnBehalfOfPrincipal) {
				payloadItem = { ...payloadItem, enrolmentPrincipals: principalItems.filter(p => p.$selected).map(p => ({ principalId: p.id })) }
			}

			return payloadItem
		})

		const [error] = await API_ENROLMENTS.create(systemPayload)

		setLoading(false)

		if (error) {
			return addToastMessage(error?.message, toastType.error)
		}

		let dataInfo;

		if (getOrganisationIds?.some(organisation => organisation !== user.organizationId)) {
			dataInfo = {
				type: "provider",
				dataElement: currentItem.dataElement,
				enrolmentType: enrolmentType
			}

			return goTo(`/use-cases/${useCaseId}/pitstop/success`, dataInfo)
		}

		dataInfo = {
			type: "no-provider",
			dataElement: currentItem.dataElement,
			enrolmentType: enrolmentType
		};

		goTo(`/use-cases/${useCaseId}/pitstop/success`, dataInfo);
	}

	function getTypeName() {
		if (enrolmentType.toUpperCase() === EnrolmentTypes.provider) {
			return "Provide";
		} else if (enrolmentType.toUpperCase() === EnrolmentTypes.consumer) {
			return "Consume";
		} else if (enrolmentType.toUpperCase() === EnrolmentTypes.provideOnBehalf) {
			return "Provide on behalf of";
		} else if (enrolmentType.toUpperCase() === EnrolmentTypes.consumeOnBehalf) {
			return "Consume on behalf of";
		} else if (enrolmentType.toUpperCase() === EnrolmentTypes.provideOnBehalfOfPrincipal) {
			return isAgentPrincipalPhase2Enabled ? "provide on behalf (Principal) for" : "Provide as agent for";
		} else if (enrolmentType.toUpperCase() === EnrolmentTypes.consumeOnBehalfOfPrincipal) {
			return isAgentPrincipalPhase2Enabled ? "consume on behalf (Principal) for" : "Consume as agent for";
		}
	}

	return (
		<>
			<div className="title-header">
				<div className="title-header__left">
					<div className="title-header_title" data-testid="page-title">{pageTitle}</div>
				</div>
				<div className="title-header__right" />
			</div>

			<Stepper steps={steps} currentStep={step-1}/>

			<hr />

			{step === 1 && (
				<>
					<div className="layout-default__desc__data__element">
						Choose Pitstop to {getTypeName()}{" "}
						{currentItem?.dataElement.dataElementName}{!isAgentPrincipalPhase2Enabled ? ' with' : '.'}
					</div>
					<SystemList dataItem={systemItems} />
				</>
			)}

			{isOnBehalfOfPrincipal ? <>
				{step === 2 && (
					<>
						<div className="use-cases__search__box">
							<Input
								maxWidth="334"
								fullWidth
								placeholder="Search by Principal name or UEN"
								prefix={<IconSearch />}
								value={searchBoxValue || ""}
								onChange={(e) => { handleSearch(e) }}
								cancelIcon={<IconClose />} />
							<div>
								{principalItems?.filter(s => s?.$selected)?.length > 0 && (
									<span>{principalItems?.filter(s => s?.$selected)?.length || 0} Selected</span>
								)}
							</div>
						</div>
						<PrincipalTableList
							principals={principalItems}
							setPrincipals={setPrincipalItems}
							searchPrincipal={searchBoxValue}
							pagination={true}
							titleHeader="Principals"
						/>
					</>
				)}
				{step === 3 && (
					<>
						<Confirmation />
					</>
				)}
			</> : <>
				{step === 2 && (
					<>
						<Confirmation />
					</>
				)}
			</>}
			<div className='DS__layout__footer'>
				<div className="layout-default__sticky-actions">
					<div className="layout-default__sticky-actions__left">
						<div className="layout-default--margin-right">
							<Button
								text="Cancel"
								ghost
								noborder
								submit={() => onBack()}
							/>
						</div>
					</div>
					<div className="layout-default__sticky-actions__right layout-default--flex">
						{step > 1 && (
							<div className="layout-default--margin-right">
								<Button
									text="Previous"
									ghost
									submit={() => {
										prevStep();
									}}
								/>
							</div>
						)}

						{step === steps.length ? (
							<Button
								sizing="Medium"
								text="Submit"
								full
								maxWidth="154"
								submit={() => confirm()}
								disabled={
									isSubmitBtnDisabled(
										loading,
										enrolmentType,
										selectedSystemItem,
										pitstopList,
									)
								}
								loading={loading}
							/>
						) : (
							<Button
								text="Next"
								disabled={
									step === 1 && !systemItems?.some((item) => item.$selected === true) ||
									step === 2 && isOnBehalfOfPrincipal && !principalItems?.some((item) => item.$selected === true)
								}
								submit={() => {
									if (step === 1)
										getSortedData();
									nextStep();
								}}
							/>
						)}
					</div>
				</div>
			</div>

			<Modal visible={visibleSystemInfo} variant="medium" close={hideSystemInfo}>
				<CardConfigure	>
					<CardConfigure.Header>
						<CardConfigure.Header.Title>
							<div className="card-configure__header__left">
								<div className="card-configure__title">
									<div className="card-configure__title__left">
										<div className="persona-info-modal__icon">
											<IconGear />
										</div>
									</div>
									<div className="card-configure__title__right">
										<div className="card-configure__title__right__top">
											About {currenSystemItem?.systemName || "-"}
										</div>
									</div>
								</div>
							</div>
						</CardConfigure.Header.Title>
					</CardConfigure.Header>
					<CardConfigure.Body>
						<CardConfigure.Body.Col>
							<div className="persona-info-modal">
								<div className="persona-info-modal__title">
									Provider by{" "}
									{currenSystemItem?.organisation?.name ||
										"-"}
								</div>
								<div className="persona-info-modal__value">
									<div className="layout-card__content__text">
										{currenSystemItem?.description || "-"}
									</div>
								</div>
							</div>
						</CardConfigure.Body.Col>
					</CardConfigure.Body>
				</CardConfigure>
			</Modal>

			<Toast toastList={toastList} />
		</>
	);
};
export default Page;
